import {
  FlexColumn,
  TranscendColor,
  TranscendWhite,
  useQueryParam,
} from '@main/core-ui';
import { SkipLinkIds } from '@main/pc-types';
import Hero from '@main/privacy-center/src/components/Hero';
import {
  selectDisabledPrivacyCenterAboutTranscend,
  selectRequiredPrivacyCenter,
} from '@main/privacy-center/src/containers/App/selectors';
import { Header } from '@main/privacy-center/src/containers/Header';
import { Sidebar } from '@main/privacy-center/src/containers/Sidebar';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useSideMenuTabs } from '../../containers/Sidebar/hooks';
import { heroPageMessages } from './messages';
import {
  ColWrapper,
  ContentGrid,
  HeaderWrapper,
  HeroWrapper,
  LogoImageWrapper,
  LogoLink,
  LogoWrapper,
  RowWrapper,
  SkipLinksWrapper,
} from './wrappers';

/**
 * Render a page wrapped in hero
 */
export const HeroPage: React.FC = ({ children }) => {
  const {
    organization: { name: organizationName },
    assets: { logoOnSolid, logo, hero },
    home,
    theme,
  } = useSelector(selectRequiredPrivacyCenter);
  const pageTabs = useSideMenuTabs();
  const { formatMessage } = useIntl();
  const { value: simple } = useQueryParam({ name: 'simple' });
  const useSimpleVersion = simple === 'true';
  const disabledPrivacyCenterAboutTranscend = useSelector(
    selectDisabledPrivacyCenterAboutTranscend,
  );

  const shouldShowSideMenu =
    pageTabs.length > 0 || !disabledPrivacyCenterAboutTranscend;
  const logoToUse = shouldShowSideMenu ? logoOnSolid || logo : logo;
  const logoAlt = logoToUse
    ? formatMessage(logoToUse.alt || heroPageMessages.organizationLogoAlt, {
        organizationName,
      })
    : formatMessage(heroPageMessages.transcendLogoAlt);

  const handleSkipLink = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    const destinationId = event.currentTarget.getAttribute('href');
    if (!destinationId) {
      return;
    }
    const destinationEl: HTMLElement = document.querySelector(
      destinationId,
    ) as HTMLElement;
    if (destinationEl) {
      destinationEl.scrollIntoView();
      destinationEl.focus();
      // account for fixed header, if destination is not navigation
      if (destinationId !== `#${SkipLinkIds.Navigation}`) {
        const scrolledY = window.scrollY;
        if (scrolledY) {
          const headerHeight = document
            .getElementById(SkipLinkIds.Navigation)
            ?.getBoundingClientRect().height;
          window.scroll(0, scrolledY - (headerHeight || 0));
        }
      }
    }
  };

  return (
    <FlexColumn style={{ flexGrow: 1 }}>
      {!useSimpleVersion && (
        <>
          <HeroWrapper>
            <SkipLinksWrapper>
              <li>
                <a
                  className="skip-link"
                  href={`#${SkipLinkIds.Navigation}`}
                  onClick={handleSkipLink}
                >
                  {formatMessage(heroPageMessages.skipToNavigation)}
                </a>
              </li>
              <li>
                <a
                  className="skip-link"
                  href={`#${SkipLinkIds.Sidebar}`}
                  onClick={handleSkipLink}
                >
                  {formatMessage(heroPageMessages.skipToSidebar)}
                </a>
              </li>
              <li>
                <a
                  className="skip-link"
                  href={`#${SkipLinkIds.Main}`}
                  onClick={handleSkipLink}
                >
                  {formatMessage(heroPageMessages.skipToMain)}
                </a>
              </li>
            </SkipLinksWrapper>
            <Hero asset={hero} />
          </HeroWrapper>
          <RowWrapper
            id={SkipLinkIds.Navigation}
            aria-label={formatMessage(heroPageMessages.navLabel)}
            tabIndex={-1}
          >
            <ContentGrid>
              <LogoWrapper $showSideMenu={shouldShowSideMenu}>
                <LogoLink
                  href={formatMessage(home)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LogoImageWrapper
                    src={
                      logoToUse?.src ??
                      (shouldShowSideMenu ? TranscendWhite : TranscendColor)
                    }
                    alt={logoAlt}
                    className="sidebar-org-logo"
                  />
                </LogoLink>
              </LogoWrapper>
              <HeaderWrapper $showSideMenu={shouldShowSideMenu}>
                <Header />
              </HeaderWrapper>
            </ContentGrid>
          </RowWrapper>
        </>
      )}
      {useSimpleVersion ? (
        children
      ) : (
        // Hide sidebar column if no side menu
        <ContentGrid
          style={{
            ...(shouldShowSideMenu ? {} : { gridTemplateColumns: '0fr 1fr' }),
          }}
        >
          <div
            style={{
              overflow: 'hidden',
              opacity: 1,
              transition: 'opacity 0.2s ease-in-out',
              ...(shouldShowSideMenu ? {} : { opacity: 0 }),
            }}
          >
            <ColWrapper>
              <Sidebar tabs={pageTabs} />
            </ColWrapper>
          </div>
          <div style={{ backgroundColor: `${theme.colors.bgAccent}` }}>
            {children}
          </div>
        </ContentGrid>
      )}
    </FlexColumn>
  );
};

export default HeroPage;
